import { safeJsonStringify } from '../json';

type UrlOptions = {
  // Path to append to the URL
  path?: string | number;
  // Query parameters to append to the URL
  queryParams?: Record<
    string,
    | unknown
    | null
    | undefined
    | string
    | number
    | string[]
    | (string | number)[]
    | Record<string, unknown>[]
  >;
  // Whether to encode the URL
  encodeURI?: boolean;
};

/**
 * Builds a query string from an object of query parameters
 * @param url - the base URL (not needed, but kept for consistency)
 * @param queryParams - the query parameters to append to the URL
 */
function buildQueryString(_url: string, queryParams: NonNullable<UrlOptions['queryParams']>) {
  const queryString: string[] = [];

  for (const key in queryParams) {
    const value = queryParams[key];
    if (value === null || value === undefined) {
      continue;
    }
    if (Array.isArray(value)) {
      if (typeof value[0] === 'object') {
        // [{key: "param_1", value: "value_1"}, {key: "param_2", value: "value_2"}] --> "[{key: "param_1", value: "value_1"}, {key: "param_2", value: "value_2"}]"
        // Ex. Authorisation params in OAuth2 in HTTP Application
        queryString.push(`${key}=${safeJsonStringify(value, '', 0)}`);
      } else {
        // ["value_1", "value_2"] --> "value_1,value_2"
        // Ex. unknown
        queryString.push(`${key}=${value.join(',')}`);
      }
    } else if (typeof value === 'object') {
      // {param_1: "value_1"} --> "%7B%22param_1%22%3A%22value_1%22%7D"
      queryString.push(`${key}=${encodeURIComponent(safeJsonStringify(value, '', 0))}`);
    } else {
      queryString.push(`${key}=${value}`);
    }
  }

  if (queryString.length === 0) {
    return '';
  }

  return `?${queryString.join('&')}`;
}

/**
 * Appends a path to a URL removing any unnecessary slashes in between
 * @param url - the base URL
 * @param path - the path to append to the base URL
 */
function appendPath(url: string, path: NonNullable<UrlOptions['path']>) {
  let cleanUrl = url;
  let cleanPath = String(path);

  if (url.endsWith('/')) {
    cleanUrl = url.slice(0, -1);
  }

  if (cleanPath.startsWith('/')) {
    cleanPath = cleanPath.slice(1);
  }

  return `${cleanUrl}/${cleanPath}`;
}

/**
 * Builds a full URL from a base URL and a path removing any unnecessary slashes in between
 * Inspired from https://github.com/meabed/build-url-ts
 * @param url - the base URL
 * @param options - the path and/or query params to append to the base URL
 */
export function buildUrl(url: string, options?: UrlOptions) {
  let builtUrl: string = url;

  if (options?.path) {
    const path = options.encodeURI ? encodeURIComponent(options.path) : options.path;
    builtUrl = appendPath(builtUrl, path);
  }

  if (options?.queryParams) {
    const queryString = buildQueryString(builtUrl, options.queryParams);

    const encodedQueryString = options.encodeURI ? encodeURIComponent(queryString) : queryString;

    if (queryString) {
      builtUrl += encodedQueryString;
    }
  }

  return builtUrl;
}
